import React, { useEffect, useState } from 'react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command'
import { useNavigate } from '@tanstack/react-router'
import { 
  Home, 
  CircleGauge, 
  GitCompare, 
  Activity, 
  LineChart, 
  MonitorDot, 
  Users,
  Search
} from 'lucide-react'

export const GlobalCommandPalette = () => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Toggle Command Palette with Ctrl+K or Cmd+K
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }

      // Shift + Letter Shortcuts
      if (e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && e.target instanceof Element && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        switch (e.key.toLowerCase()) {
          case 'h':
            e.preventDefault()
            navigate({ to: '/home' })
            break
          case 'd':
            e.preventDefault()
            navigate({ to: '/dashboard' })
            break
          case 'c':
            e.preventDefault()
            navigate({ to: '/crossover' })
            break
          case 'v':
            e.preventDefault()
            navigate({ to: '/variance' })
            break
          case 'a':
            e.preventDefault()
            navigate({ to: '/analysis' })
            break
          case 'i':
            e.preventDefault()
            navigate({ to: '/inspector', search: (prev: any) => prev })
            break
          case 'u':
            e.preventDefault()
            navigate({ to: '/users' })
            break
        }
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [navigate])

  const runCommand = (command: () => void) => {
    setOpen(false)
    command()
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/home' }))}>
            <Home className="mr-2 h-4 w-4" />
            <span>Home</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧H</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/dashboard' }))}>
            <CircleGauge className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧D</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/crossover' }))}>
            <GitCompare className="mr-2 h-4 w-4" />
            <span>Crossover</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧C</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/variance' }))}>
            <Activity className="mr-2 h-4 w-4" />
            <span>Variance</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧V</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/analysis' }))}>
            <LineChart className="mr-2 h-4 w-4" />
            <span>Analysis</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧A</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/inspector', search: (prev: any) => prev }))}>
            <MonitorDot className="mr-2 h-4 w-4" />
            <span>Inspector</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧I</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate({ to: '/users' }))}>
            <Users className="mr-2 h-4 w-4" />
            <span>Users</span>
            <span className="ml-auto text-xs text-slate-500 tracking-widest bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">⇧U</span>
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Symbols">
          <CommandItem>
            <Search className="mr-2 h-4 w-4" />
            <span>Search Symbol...</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
